import { Alert, Button, Stack, Typography } from "@mui/material";
import React, { useState } from "react";
import Modal, { CommonModalProps } from "..";
import { basketService, orderService } from "../../../Core/Api/services";
import { AclMatch, UserHasAcls } from "../../../Utils/helpers";
import { AclTypes } from "../../../Core/Api/Types/acl-types";
import { OrderManualTaskTemplateTypes } from "../../../Core/Api/Types/order-manual-task-template-types";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import { useNavigate } from "react-router";
import { routePaths } from "../../../Router/router";
import FormInputText from "../../Forms/FormInputText/form-input-text";
import { GetSectionIsValid } from "../../../Redux/Reducers/redux-slice-form-validation";
import { useAppSelector } from "../../../Redux/redux-store";
import useBasketActions from "../../../Utils/Hooks/useBasketActions";
import { useTranslation } from "react-i18next";
import { OrderProductType } from "../../../Core/Api/Types/order-product-type";

export type ModalOrderFileFixingActionElementProps = {
  title?: string;
  width?: number;
  objectId: number;
  onConfirm?: () => void;
};

export enum FileFixingMode {
  Quote,
  Work,
}

export type ModalConfirmActionProps = CommonModalProps<ModalOrderFileFixingActionElementProps>;

const ModalOrderFileFixingAction = React.forwardRef<any, ModalConfirmActionProps>((props, ref) => {
  const { t } = useTranslation();
  const { closeModal, onConfirm, objectId, ...modalProps } = props;
  const { title, width } = modalProps;
  const enqueueCustomSnackbar = useCustomSnackbar();
  const navigate = useNavigate();
  const { reloadBasketPanel } = useBasketActions();

  const mode: FileFixingMode = !UserHasAcls([AclTypes.aclAdmin, AclTypes.aclSuperUser], AclMatch.OneAtLeast)
    ? FileFixingMode.Quote
    : FileFixingMode.Work;

  const modalTitle =
    title ??
    (mode === FileFixingMode.Work
      ? t("modal:title.makeFileFixingWorkOnAsset", { objectId })
      : t("modal:title.requestFileFixingQuoteOnAsset", { objectId }));

  const [comment, setComment] = useState("");

  const isFormValid = useAppSelector(GetSectionIsValid("form-quote", "sectionComment"));

  const handleConfirmClick = async (goToBasket: boolean) => {
    switch (mode) {
      case FileFixingMode.Quote:
        orderService
          .CreateQuoteRequestOrder({
            assetId: objectId,
            templateType: OrderManualTaskTemplateTypes.FileFixing,
            comment: comment,
          })
          .then((response) => {
            if (response.success) enqueueCustomSnackbar(t("alert:success.yourQuoteRequestWillBeTreated"), "success");
          });
        break;
      case FileFixingMode.Work:
        // Find manual tasks product
        const product = await orderService.GetDeliveryProductsList().then((response) => {
          return response.products?.find((p) => p.type === OrderProductType.ManualTasks);
        });

        if (!product) {
          enqueueCustomSnackbar(t("alert:error.noManualTasksFound"), "error");
          return;
        }

        // Add to basket + set directives
        basketService
          .AddItemToBasket({
            objectId: objectId,
            episodesNbList: null,
            externalContractId: null,
          })
          .then((add) => {
            if (add.success) {
              const objectId = add.items?.[0]?.id;

              if (!objectId) return;

              basketService
                .SetItemsDirectives({
                  directives: [
                    {
                      itemId: objectId,
                      productId: product.id,
                      contentId: null,
                      broadcasterId: null,
                      manualTaskTemplateId: null,
                      includeOnlyAvailableValues: true,
                    },
                  ],
                })
                .then((directives) => {
                  if (directives.success) {
                    reloadBasketPanel();
                    if (goToBasket) navigate(routePaths.basket.path);
                  } else if (directives.message) enqueueCustomSnackbar(directives.message, "error");
                });
            }
          });

        break;
    }
    onConfirm && onConfirm();
    closeModal();
  };

  const handleCancelClick = () => {
    closeModal();
  };

  return (
    <Modal
      title={modalTitle}
      reference={ref}
      closeModal={handleCancelClick}
      width={width ? width : 600}
      footer={
        <Stack direction="row" justifyContent="space-between">
          <Button onClick={handleCancelClick}>{t("common:cancel")}</Button>
          <Stack direction="row" spacing={1}>
            <Button
              color="success"
              variant="outlined"
              onClick={() => handleConfirmClick(false)}
              disabled={mode === FileFixingMode.Quote && !isFormValid}
            >
              {t("common:confirm")}
            </Button>
            {mode === FileFixingMode.Work && (
              <Button color="success" variant="outlined" onClick={() => handleConfirmClick(true)}>
                {t("button:confirmAndGotoBasket")}
              </Button>
            )}
          </Stack>
        </Stack>
      }
    >
      {mode === FileFixingMode.Work && (
        <Stack>
          <Typography variant="h6" component="span" color="text.secondary">
            {t("modal:modalOrderFileFixingAction.body.quoteProposalWorkflow")}
          </Typography>

          <Alert sx={{ mt: 1 }}>{t("alert:info.afterConfirmAssetBeAddedToBasket")}</Alert>
        </Stack>
      )}
      {mode === FileFixingMode.Quote && (
        <Stack>
          <FormInputText
            multiline
            value={comment}
            onChange={(e) => setComment(e.currentTarget.value)}
            placeholder={t("field:placeholder.comment")}
            rows={3}
            validationProps={{
              formId: "form-quote",
              formSectionId: "sectionComment",
              fieldId: "comment",
              initialState: false,
              required: true,
              minLength: 3,
            }}
          />
          {isFormValid && <Alert sx={{ mt: 1 }}>{t("alert:info.clickOnConfirm")}</Alert>}
        </Stack>
      )}
    </Modal>
  );
});

export default ModalOrderFileFixingAction;
